import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdBanner from "@/components/AdBanner";
import Podium from "@/components/Podium";
import PilotAvatar from "@/components/PilotAvatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { gruppoColor } from "@/lib/race";

const TABS = [
  { key: "prove", label: "Prove", pointsKey: "points_prove" },
  { key: "gara", label: "Gara", pointsKey: "points_gara" },
  { key: "weekend", label: "Totale Weekend", pointsKey: "total_points" },
];

function TeamPopover({ entry }) {
  return (
    <div className="w-72">
      <p className="font-racing text-xs font-bold text-white uppercase tracking-wide mb-3">
        {entry.user.team_name || entry.user.username}
      </p>
      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {entry.team.length === 0 && (
          <p className="text-xs text-gray-500">Nessuna squadra schierata.</p>
        )}
        {entry.team.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between gap-2 rounded-lg border border-[#3F3F4C] bg-[#121214] px-2.5 py-1.5"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-1.5 h-6 rounded-full shrink-0" style={{ background: gruppoColor(p.gruppo) }} />
              <PilotAvatar pilot={p} size={28} />
              <div className="min-w-0">
                <p className="text-xs font-body-sport font-semibold text-white truncate flex items-center gap-1">
                  {p.cognome}
                  {p.is_captain && <Star className="w-3 h-3 text-[#FFB703] fill-[#FFB703] shrink-0" />}
                </p>
                <p className="text-[9px] text-gray-500">{p.categoria || p.gruppo} • {p.prezzo} CR</p>
              </div>
            </div>
            <span className="font-time text-xs font-bold text-[#FFB703] shrink-0">
              {p.punti_effettivi}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("weekend");
  const { data: entries = [] } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: async () => (await api.get("/leaderboard")).data,
    refetchInterval: 10000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const ufficiale = settings?.race?.gara_finita === true;
  const pointsKey = TABS.find((t) => t.key === tab)?.pointsKey || "total_points";

  const sorted = useMemo(
    () =>
      [...entries]
        .sort((a, b) => (b.user[pointsKey] ?? 0) - (a.user[pointsKey] ?? 0))
        .map((e, i) => ({ ...e, posizione: i + 1 })),
    [entries, pointsKey]
  );

  const podiumItems = sorted.slice(0, 3).map((e) => ({
    pos: e.posizione,
    title: e.user.team_name || e.user.username,
    points: e.user[pointsKey] ?? 0,
  }));

  return (
    <div data-testid="leaderboard-page" className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-racing font-bold text-xl sm:text-2xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
          Classifica Generale
        </h2>
        <span
          data-testid="leaderboard-status-badge"
          className={`font-body-sport text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border ${
            ufficiale
              ? "text-[#22C55E] border-[#22C55E]/50 bg-[#22C55E]/10"
              : "text-[#FFB703] border-[#FFB703]/50 bg-[#FFB703]/10"
          }`}
        >
          {ufficiale ? "Ufficiale — Decretata" : "Ufficiosa — Gara in corso"}
        </span>
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] p-1 max-w-xl" data-testid="leaderboard-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`leaderboard-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[100px] rounded-lg px-3 py-2.5 min-h-[44px] font-body-sport font-bold text-[11px] tracking-widest uppercase transition-colors ${
              tab === t.key ? "bg-[#FF1E27] text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Podium items={podiumItems} testidPrefix="podium" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] overflow-hidden">
            <div className="grid grid-cols-[3rem_1fr_5rem] sm:grid-cols-[3.5rem_1fr_1fr_6rem] gap-2 px-4 py-3 border-b border-[#3F3F4C] font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              <span>Pos</span>
              <span>Pilota Fantasy</span>
              <span className="hidden sm:block">Scuderia</span>
              <span className="text-right">Punti {TABS.find((t) => t.key === tab)?.label}</span>
            </div>
            {sorted.map((e, i) => (
              <motion.div key={e.user.id} layout transition={{ type: "spring", stiffness: 120, damping: 20 }}>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid={`leaderboard-row-${e.user.id}`}
                      className={`w-full grid grid-cols-[3rem_1fr_5rem] sm:grid-cols-[3.5rem_1fr_1fr_6rem] gap-2 items-center px-4 py-3 text-left transition-colors hover:bg-[#242429] ${
                        i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"
                      } ${e.user.id === user?.id ? "border-l-2 border-[#FF1E27]" : ""}`}
                    >
                      <span
                        className={`font-racing font-black text-lg ${
                          e.posizione === 1
                            ? "text-[#FFD700]"
                            : e.posizione === 2
                            ? "text-[#C0C0C0]"
                            : e.posizione === 3
                            ? "text-[#CD7F32]"
                            : "text-gray-500"
                        }`}
                      >
                        {e.posizione}
                      </span>
                      <span className="font-body-sport font-semibold text-white text-sm truncate">
                        {e.user.username}
                        {e.user.id === user?.id && (
                          <span className="ml-2 text-[9px] text-[#FF1E27] tracking-widest">TU</span>
                        )}
                        {e.user.badges?.map((b) => (
                          <span key={b.key} title={b.nome} className="ml-1" data-testid={`badge-${b.key}-${e.user.id}`}>
                            {b.icona}
                          </span>
                        ))}
                      </span>
                      <span className="hidden sm:block text-xs text-gray-500 truncate">
                        {e.user.team_name}
                      </span>
                      <span className="font-time font-bold text-[#FFB703] text-right text-sm">
                        {e.user[pointsKey] ?? 0}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-[#1A1A1E] border-[#3F3F4C] p-4" data-testid={`team-popover-${e.user.id}`}>
                    <TeamPopover entry={e} />
                  </PopoverContent>
                </Popover>
              </motion.div>
            ))}
            {sorted.length === 0 && (
              <p className="text-center text-gray-500 text-sm py-10" data-testid="leaderboard-empty">
                Nessun utente in classifica.
              </p>
            )}
          </div>
        </div>
        <div className="space-y-6">
          <AdBanner slot="sidebar" />
        </div>
      </div>
    </div>
  );
}
